import { useContext, useEffect, useState } from "react";
import { faPenToSquare, faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import services from "../../../../services/services";
import { Urls } from "../../../../services/Urls";
import _ from "underscore";
import { callautopopulate } from "../../../../services/callautopop";
import { ToastContext } from "../../../../context/ToastContext";

export const useTaskTypeHierarchy = () => {

    const { showInfo, showSuccess, showError } = useContext(ToastContext);
    const [issueTypes, setIssueTypes] = useState({ all: [], filtered: [] });
    const [formValues, setFormValues] = useState({ visible: false });
    const [loader, setLoader] = useState({ grid: false, btn: false });
    const [rowData, setRowData] = useState([]);
    const columns = [
        {
            key: "hierarchy_level",
            label: "Level",
            style: { width: "5%", textAlign: "center" },
            headerStyle: { textAlign: "center" }
        },
        {
            key: "level_name",
            label: "Level Name",
            style: { width: "30%" }
        },
        {
            key: "jira_work_types",
            label: "Jira Work Types",
            style: { width: "55%" },
            render: (row) => {
                const Compo = () => {
                    const value = row?.jira_work_types?.sort((a, b) => a?.issue_type_name?.localeCompare(b?.issue_type_name));
                    const [expand, setExpand] = useState(false);
                    const [sorted, setSorted] = useState(value?.slice(0, 3));

                    return (
                        <div className="d-flex gap-2">
                            <div className="py-2 d-flex gap-2 flex-wrap align-items-center w90">
                                {sorted?.map((item, idx) => (
                                    <div key={idx} className="px-2 round-small-section" style={{ width: "fit-content"}}>{item?.issue_type_name}</div>
                                ))}
                                {(row?.jira_work_types?.length > 3 && !expand) && <div className="text-primary c-pointer text-decoration-underline" onClick={() => { setExpand(!expand); if (expand) { setSorted(value?.slice(0, 3)); } else { setSorted(value); } }}>+ {row?.jira_work_types?.length - 3} more</div>}
                                {expand && <div className="c-pointer px-2 rounded-circle text-primary mt-2" title="Show Less" style={{ backgroundColor: "#efefef" }} onClick={() => { setExpand(false); setSorted(value?.slice(0, 3)); }}><FontAwesomeIcon icon="fa-solid fa-arrow-up" /></div>}
                            </div>
                        </div>
                    )
                }
                return <Compo />
            }
        },
        {
            key: "action",
            label: "Actions",
            style: { width: "10%", textAlign: "center" },
            headerStyle: { textAlign: "center" },
            render: (row) => <div className="d-flex gap-3 align-items-center justify-content-center">
                <div className="c-pointer text-primary" title="Edit" onClick={() => { handleEdit(row); }}><FontAwesomeIcon icon={faPenToSquare} /></div>
                <div className="c-pointer text-danger" title="Delete" onClick={() => { setFormValues((prev) => ({ ...prev, isDelete: true, editData: row })) }}><FontAwesomeIcon icon={faTrashCan} /></div>
            </div>
        },
    ]

    const formFields = [
        {
            label: "Level Name",
            name: "level_name",
            col: "8",
            type: "text",
            required: true,
        },
        {
            label: "Hierarchy Level",
            name: "level",
            col: "4",
            type: "number",
            required: true,
            min: -1
        },
        {
            label: "Issue Types",
            name: "issue_types",
            col: "12",
            type: "multiselect",
            option: issueTypes?.filtered,
            multiselectType: "chip",
            required: true,
        },
    ]

    useEffect(() => {
        callautopopulate("field_configuration_issue_type")?.then(res => {
            const types = res?.field_configuration_issue_type?.map(item => { return { ...item, label: item?.issue_type_name } })?.sort((a, b) => a?.issue_type_name?.localeCompare(b?.issue_type_name));
            setIssueTypes({ all: types, filtered: types });
        })
        getTaskTypeHierarchy();
    }, [])

    const getTaskTypeHierarchy = async () => {
        try {
            setLoader((prev) => ({ ...prev, grid: true }));
            const res = await services.getInvokeRemote("GET", Urls("GETISSUETYPEHIERARCHYT"), {});
            if (res?.body?.error_status === false) {
                const data = res?.body?.data?.hierarchy_list?.sort((a, b) => b?.hierarchy_level - a?.hierarchy_level);
                let formattedData = [];
                data?.forEach(item => {
                    if (!_.findWhere(formattedData, { level_id: item?.level_id })) {
                        formattedData.push({
                            ...item,
                            jira_work_types: [{ issue_type_id: item?.issue_type_id, issue_type_name: item?.issue_type_name }]
                        })
                    } else {
                        const index = _.indexOf(formattedData, _.findWhere(formattedData, { level_id: item?.level_id }));
                        if (index !== -1 && formattedData[index]) {
                            formattedData[index]?.jira_work_types?.push({ issue_type_id: item?.issue_type_id, issue_type_name: item?.issue_type_name })
                        }
                    }
                })
                setRowData(formattedData);
                filterIssueTypes(formattedData);
            }
        } catch (error) {
            console.error("Error: ", error);
        } finally {
            setLoader((prev) => ({ ...prev, grid: false }));
        }
    }

    const saveTaskTypeHierarchy = async () => {
        try {
            if (!formValues?.level_name && !formValues?.isDelete) {
                showInfo("Info", "Please enter level name");
                return;
            } else if (formValues?.level === undefined && !formValues?.isDelete) {
                showInfo("Info", "Please enter hierarchy level");
                return;
            } else if (_.isEmpty(formValues?.issue_types) && !formValues?.isDelete) {
                showInfo("Info", "Please select atleast issue type");
                return;
            } else if (formValues?.level < -1 && !formValues?.isDelete) {
                showInfo("Info", "Hierarchy level cannot be less than -1");
                return;
            } else if ((_.isEmpty(formValues?.editData) || formValues?.editData?.hierarchy_level !== parseInt(formValues?.level)) && !_.isEmpty(_.findWhere(rowData, { hierarchy_level: parseInt(formValues?.level) }))) {
                showInfo("Info", `Hierarchy level ${formValues?.level} already exists`);
                return;
            }
            setLoader((prev) => ({ ...prev, btn: true }));
            const payload = {
                level_id: formValues?.editData?.level_id || 0,
                level_name: formValues?.level_name || "",
                hierarchy_level: parseInt(formValues?.level) || 0,
                issue_type_ids: _.pluck(formValues?.issue_types, "issue_type_id")?.toString() || "",
                is_deleted: formValues?.isDelete || false
            }
            const res = await services.getInvokeRemote("POST", Urls("UPSERTISSUETYPESHIERARCHY"), payload);
            if (res?.body?.error_status === false) {
                showSuccess("Success", res?.body?.message);
                getTaskTypeHierarchy();
                setFormValues({ visible: false });
            }
        } catch (error) {
            console.error("Error: ", error);
        } finally {
            setLoader((prev) => ({ ...prev, btn: false }));
        }
    }

    const handleEdit = (row) => {
        let types = [];
        row?.jira_work_types?.forEach(item => {
            const findData = _.findWhere(issueTypes?.all, { issue_type_id: item?.issue_type_id });
            findData && types.push(findData);
        })
        setFormValues((prev) => ({ ...prev, editData: row, visible: true, level_name: row?.level_name, level: row?.hierarchy_level, issue_types: types }));
        filterIssueTypes(rowData, types)
    }

    const filterIssueTypes = (rowdata, additionalTypes) => {
        const usedIssueTypes = _.pluck(rowdata ? rowdata : rowData, "jira_work_types")?.flat();
        const unUsed = _.filter(issueTypes?.all, item => !_.pluck(usedIssueTypes, "issue_type_id")?.includes(item?.issue_type_id));
        const additional = additionalTypes ? additionalTypes?.sort((a, b) => a?.issue_type_name?.localeCompare(b?.issue_type_name)) : [];
        setIssueTypes((prev) => ({ ...prev, filtered: [...additional, ...unUsed] }))
    }

    return { columns, rowData, loader, getTaskTypeHierarchy, formValues, setFormValues, formFields, saveTaskTypeHierarchy, filterIssueTypes }
}